import React, { useEffect, useMemo } from 'react'
import Text from 'core-system/Text'
import Radio from 'core-system/Radio'
import SwitchToggle from 'core-system/SwitchToggle'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import { NumberFormatter, NumberInput } from 'core-system/Input/NumberInput'
import ProgramEditingSubSection from './Components/ProgramEditingSubSection'
import ProgramOption from './Components/ProgramOption'
import {
  TransitAgency,
  TransitPassDeliveryMechanism,
  TransitPassPurchaseType,
  transitPassType,
} from '../Shared/types'
import { TRANSIT_PASS_INITIAL_STATE } from './ProgramEditingSlice'
import { Merchant } from 'redux/programs/programsTypes'
import styled from 'styled-components'

const SectionLabel = styled(Text)`
  margin-bottom: 1rem;
`

const TopUpHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const transitAgencyToMerchant = (agency: TransitAgency): Merchant => ({
  id: agency.id,
  name: agency.name,
  iconUrl: agency.iconUrl,
  websiteUrl: agency.websiteUrl || '',
  modeTypes: [agency.region],
  description: '',
  fleetPartner: false,
  metroAreas: [],
  countries: [],
  modeIds: [],
  partnershipDescription: '',
  refundPolicies: '',
  setupInstructions: '',
  showUser: true,
  tags: [],
  pricingTypes: [],
})

interface TransitPassProgramEditingProps {
  transitPass: transitPassType
  changePurchaseType: (value: TransitPassPurchaseType) => void
  changeWalletTopUpEnabled: (value: boolean) => void
  changeBudget: (value: number) => void
  changeDeliveryMechanism: (value: TransitPassDeliveryMechanism) => void
  changeAgencyIds: (value: string[]) => void
  changeAgenciesEnabled: (value: boolean) => void
  getTransitAgencies: () => void
}

const TransitPassProgramEditing = ({
  transitPass = TRANSIT_PASS_INITIAL_STATE,
  changePurchaseType,
  changeWalletTopUpEnabled,
  changeBudget,
  changeDeliveryMechanism,
  changeAgencyIds,
  changeAgenciesEnabled,
  getTransitAgencies,
}: TransitPassProgramEditingProps) => {
  const availableAgencyIds = useMemo(
    () => transitPass.availableTransitAgencies.map((agency) => agency.id),
    [transitPass.availableTransitAgencies]
  )

  const agenciesMap = useMemo(
    () =>
      transitPass.availableTransitAgencies.reduce<Dictionary<Merchant>>(
        (acc, agency) => {
          acc[agency.id] = transitAgencyToMerchant(agency)
          return acc
        },
        {}
      ),
    [transitPass.availableTransitAgencies]
  )

  useEffect(() => {
    getTransitAgencies()
  }, [])

  return (
    <>
      <ProgramEditingSubSection>
        <SectionLabel variant='h5'>Purchase Type</SectionLabel>
        <FlexContainer flexDirection='column'>
          <Radio
            active={transitPass.purchaseType === 'FIXED'}
            onClick={() => changePurchaseType('FIXED')}
            label='Fixed Pass Purchase'
            marginBottom='0.75rem'
          />
          <Radio
            active={transitPass.purchaseType === 'EMPLOYEE_CHOICE'}
            onClick={() => changePurchaseType('EMPLOYEE_CHOICE')}
            label='Employees can choose'
          />
        </FlexContainer>
      </ProgramEditingSubSection>

      <ProgramEditingSubSection>
        <TopUpHeader>
          <Text variant='h5'>Wallet Balance Top-Up</Text>
          <SwitchToggle
            active={transitPass.walletTopUpEnabled}
            onClick={() =>
              changeWalletTopUpEnabled(!transitPass.walletTopUpEnabled)
            }
          />
        </TopUpHeader>
        {transitPass.walletTopUpEnabled && (
          <>
            <Text
              variant='body2'
              textColor={palette.text.secondary}
              marginBottom='0.5rem'
            >
              Employer Contribution Per Commuter
            </Text>
            <NumberInput
              padding='1rem'
              onValueChange={(value) => changeBudget(value * 100)}
              value={transitPass.budget / 100}
              iconSize={20}
              incrementVal={5}
              maxHeight='2.5rem'
            >
              <NumberFormatter decimalScale={0} fontVariant='h5' />
            </NumberInput>
          </>
        )}
      </ProgramEditingSubSection>

      <ProgramEditingSubSection>
        <SectionLabel variant='h5'>Pass Delivery mechanism</SectionLabel>
        <FlexContainer flexDirection='column'>
          <Radio
            active={transitPass.passDeliveryMechanism === 'VIRTUAL'}
            onClick={() => changeDeliveryMechanism('VIRTUAL')}
            label='Virtual'
            marginBottom='0.75rem'
          />
          <Radio
            active={transitPass.passDeliveryMechanism === 'PHYSICAL'}
            onClick={() => changeDeliveryMechanism('PHYSICAL')}
            label='Physical'
          />
        </FlexContainer>
      </ProgramEditingSubSection>

      <ProgramEditingSubSection isLastSection>
        <ProgramOption
          type='transitAgencies'
          active={transitPass.agenciesEnabled}
          activeMerchantIds={transitPass.transitAgencyIds}
          availableMerchantIds={availableAgencyIds}
          providersMap={agenciesMap}
          selectorModalTitle='Available Transit Agencies'
          handleProviderChange={changeAgencyIds}
          handleToggle={() =>
            changeAgenciesEnabled(!transitPass.agenciesEnabled)
          }
        />
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(TransitPassProgramEditing)
